// MessageActionsMenu.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { toggleMessageStar } from "../../actions/message";
import { EMOJI_SETS, QUICK_REACTIONS, getMyReaction } from "../../utils/messageReactions";

const getId = (value) => String(value?._id || value || "");

const VIEWPORT_GAP = 8;
const MENU_GAP = 6;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getMessageCopyText = (message) => {
    let text = String(message?.text || "");

    // Старый формат reply хранил служебную строку "↩️ Автор: цитата"
    // прямо в text. При копировании пользователю нужен только сам ответ.
    if (text.startsWith("↩️ ")) {
        const lineBreak = text.indexOf("\n");
        text = lineBreak >= 0 ? text.slice(lineBreak + 1) : "";
    }

    text = text.trim();
    // У голосового и кружка без подписи копируем расшифровку.
    if (!text && message?.voiceId && typeof message.voiceId === "object") {
        text = String(message.voiceId.transcriptText || "").trim();
    }
    return text;
};

const copyTextToClipboard = async (text) => {
    if (!text || typeof document === "undefined") return false;

    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_error) {
        // fallback ниже
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.select();

    try {
        const copied = document.execCommand("copy");
        return copied;
    } finally {
        document.body.removeChild(textarea);
    }
};

export default function MessageActionsMenu({
    open = false,
    message = null,
    isSelf = false,
    anchorPoint = null,
    onClose = null,
    onReply = null,
    onEdit = null,
    onDelete = null,
    onForward = null,
    onSelect = null,
    onReact = null,
    onToTask = null,
    onDownload = null,
    onDownloadAlbum = null,
}) {
    const dispatch = useDispatch();
    const menuRef = useRef(null);

    const currentUserId = useSelector(
        (state) => state.auth?.user?._id || state.auth?.user?.id || null
    );

    const [menuPosition, setMenuPosition] = useState({
        top: 0,
        left: 0,
        placement: "down",
        ready: false,
    });
    const [emojiExpanded, setEmojiExpanded] = useState(false);
    const [emojiTab, setEmojiTab] = useState(EMOJI_SETS[0]?.key || "faces");

    const starred = Array.isArray(message?.starredBy) &&
        message.starredBy.some((id) => getId(id) === getId(currentUserId));

    const canForward = !!message?._id &&
        !String(message._id).startsWith("local:") &&
        message?.__sendStatus !== "sending" &&
        message?.__sendStatus !== "failed";
    const copyText = getMessageCopyText(message);
    const canCopyText = !!copyText;
    const currentReaction = getMyReaction(message?.reactions, currentUserId);
    const activeEmojiSet = EMOJI_SETS.find((item) => item.key === emojiTab) || EMOJI_SETS[0];

    const positionMenu = useCallback(() => {
        const menu = menuRef.current;
        if (!menu || !anchorPoint || typeof window === "undefined") return;

        const menuWidth = menu.offsetWidth || 196;
        const menuHeight = menu.offsetHeight || 194;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const x = Number(anchorPoint.x || 0);
        const y = Number(anchorPoint.y || 0);
        const belowTop = y + MENU_GAP;
        const aboveTop = y - MENU_GAP - menuHeight;
        const hasRoomBelow = belowTop + menuHeight <= viewportHeight - VIEWPORT_GAP;
        const hasRoomAbove = aboveTop >= VIEWPORT_GAP;

        let placement = "down";
        let top = belowTop;

        if (!hasRoomBelow && hasRoomAbove) {
            placement = "up";
            top = aboveTop;
        } else if (!hasRoomBelow) {
            top = clamp(
                belowTop,
                VIEWPORT_GAP,
                Math.max(VIEWPORT_GAP, viewportHeight - menuHeight - VIEWPORT_GAP)
            );
        }

        const preferredLeft = isSelf ? x - menuWidth : x;
        const left = clamp(
            preferredLeft,
            VIEWPORT_GAP,
            Math.max(VIEWPORT_GAP, viewportWidth - menuWidth - VIEWPORT_GAP)
        );

        setMenuPosition({ top, left, placement, ready: true });
    }, [anchorPoint, isSelf]);

    useEffect(() => {
        if (!open || !message) return undefined;

        setEmojiExpanded(false);
        setEmojiTab(EMOJI_SETS[0]?.key || "faces");
        setMenuPosition((prev) => ({ ...prev, ready: false }));
        const frame = window.requestAnimationFrame(positionMenu);

        const handleOutside = (event) => {
            if (menuRef.current?.contains(event.target)) return;
            onClose?.();
        };

        const handleScroll = () => onClose?.();
        const handleResize = () => positionMenu();
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleResize);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.cancelAnimationFrame(frame);
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleResize);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, message, onClose, positionMenu]);

    useEffect(() => {
        if (!open || typeof window === "undefined") return undefined;
        const frame = window.requestAnimationFrame(positionMenu);
        return () => window.cancelAnimationFrame(frame);
    }, [emojiExpanded, emojiTab, open, positionMenu]);

    if (!open || !message || typeof document === "undefined") return null;

    const handleStar = async () => {
        await dispatch(toggleMessageStar(message._id, !starred));
        onClose?.();
    };

    const handleCopyText = async () => {
        if (!canCopyText) return;

        const copied = await copyTextToClipboard(copyText);
        if (copied) onClose?.();
    };

    const handleReactionPick = (emoji) => {
        if (!onReact || !emoji) return;
        Promise.resolve(onReact(emoji)).catch(() => undefined);
        onClose?.();
    };

    const menuNode = (
        <div
            ref={menuRef}
            className={[
                "message-actions-AO__menu",
                "is-portal",
                `is-${menuPosition.placement}`,
                menuPosition.ready ? "is-ready" : "",
            ].filter(Boolean).join(" ")}
            style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
            }}
            role="menu"
        >
            {!!onReact && canForward && (
                <div className="message-actions-AO__reaction-picker" data-chat-no-select="true">
                    <div className="message-actions-AO__reaction-quick">
                        {QUICK_REACTIONS.map((emoji) => (
                            <button
                                key={emoji}
                                type="button"
                                className={currentReaction === emoji ? "is-active" : ""}
                                onClick={() => handleReactionPick(emoji)}
                                title={currentReaction === emoji ? `Снять реакцию ${emoji}` : `Поставить реакцию ${emoji}`}
                                aria-label={currentReaction === emoji ? `Снять реакцию ${emoji}` : `Поставить реакцию ${emoji}`}
                            >
                                {emoji}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`message-actions-AO__reaction-more ${emojiExpanded ? "is-active" : ""}`}
                            onClick={() => setEmojiExpanded((value) => !value)}
                            title={emojiExpanded ? "Скрыть остальные реакции" : "Другие реакции"}
                            aria-label={emojiExpanded ? "Скрыть остальные реакции" : "Другие реакции"}
                        >
                            {emojiExpanded ? "−" : "+"}
                        </button>
                    </div>

                    {emojiExpanded && activeEmojiSet && (
                        <div className="message-actions-AO__emoji-panel">
                            <div className="message-actions-AO__emoji-tabs">
                                {EMOJI_SETS.map((set) => (
                                    <button
                                        key={set.key}
                                        type="button"
                                        className={emojiTab === set.key ? "is-active" : ""}
                                        onClick={() => setEmojiTab(set.key)}
                                        title={set.title}
                                        aria-label={set.title}
                                    >
                                        {set.icon}
                                    </button>
                                ))}
                            </div>

                            <div className="message-actions-AO__emoji-title">{activeEmojiSet.title}</div>
                            <div className="message-actions-AO__emoji-grid">
                                {activeEmojiSet.items.map((emoji) => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        className={currentReaction === emoji ? "is-active" : ""}
                                        onClick={() => handleReactionPick(emoji)}
                                        title={`Реакция ${emoji}`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <button
                type="button"
                onClick={() => {
                    onReply?.();
                    onClose?.();
                }}
            >
                Ответить
            </button>

            <button
                type="button"
                disabled={!canCopyText}
                title={canCopyText ? "Скопировать текст сообщения" : "В сообщении нет текста"}
                onClick={handleCopyText}
            >
                Скопировать текст
            </button>

            {!!onDownload && canForward && (
                <button
                    type="button"
                    onClick={() => {
                        onDownload?.();
                        onClose?.();
                    }}
                >
                    Скачать файл
                </button>
            )}

            {!!onDownloadAlbum && canForward && (
                <button
                    type="button"
                    onClick={() => {
                        onDownloadAlbum?.();
                        onClose?.();
                    }}
                >
                    Скачать все фото альбома
                </button>
            )}

            {isSelf && !!onEdit && canForward &&
                !message?.forwardedFrom?.messageId &&
                !message?.forwardedFrom?.senderName &&
                !message?.isDeleted &&
                !['voice','product_card','order_card','entity_card','system'].includes(String(message?.messageKind || 'text')) &&
                String(message?.fileType || '') !== 'voice' &&
                (!message?.entity?.type || message.entity.type === 'none') && (
                    <button type="button" onClick={() => { onEdit(); onClose?.(); }}>
                        Редактировать
                    </button>
                )}

            <button type="button" onClick={handleStar}>
                {starred ? "★ Убрать из избранного" : "☆ В избранное"}
            </button>

            <button
                type="button"
                disabled={!canForward || !onForward}
                title={!canForward ? "Дождитесь отправки сообщения" : "Переслать"}
                onClick={() => {
                    if (!canForward) return;
                    onForward?.();
                    onClose?.();
                }}
            >
                Переслать
            </button>

            {!!onToTask && (
                <button
                    type="button"
                    disabled={!canForward}
                    title={!canForward ? "Дождитесь отправки сообщения" : "Сделать из сообщения задачу или добавить его в существующую"}
                    onClick={() => {
                        if (!canForward) return;
                        onToTask?.();
                        onClose?.();
                    }}
                >
                    В задачу
                </button>
            )}

            {!!onSelect && (
                <button
                    type="button"
                    disabled={!canForward}
                    onClick={() => {
                        if (!canForward) return;
                        onSelect?.();
                        onClose?.();
                    }}
                >
                    Выбрать
                </button>
            )}

            {isSelf && onDelete && (
                <button
                    type="button"
                    className="is-danger"
                    onClick={() => {
                        onDelete?.();
                        onClose?.();
                    }}
                >
                    Удалить
                </button>
            )}
        </div>
    );

    return createPortal(menuNode, document.body);
}
